import { useState } from "react";
import {
    MapPin,
    Phone,
    Mail,
    Clock,
    Globe2,
    CheckCircle2,
    AlertCircle,
    Bus,
    ParkingCircle,
    Train,
    Send,
} from "lucide-react";
import SEO from "../components/SEO";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || "";

const subjects = [
    "Comptabilité",
    "Fiscalité",
    "TVA",
    "Salaires & RH",
    "Administrateur & gérant",
    "Création de société",
    "Autre",
];

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        consent: false,
    });
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const validate = () => {
        if (!form.name.trim()) return "Veuillez indiquer votre nom et prénom.";
        if (!/^\S+@\S+\.\S+$/.test(form.email))
            return "Veuillez indiquer une adresse email valide.";
        if (!form.subject) return "Veuillez sélectionner un sujet.";
        if (!form.message.trim()) return "Veuillez rédiger un message.";
        if (!form.consent)
            return "Vous devez accepter l'utilisation de vos données pour répondre à votre demande.";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            setErrorMsg(err);
            setStatus("error");
            return;
        }
        setErrorMsg("");
        setStatus("sending");

        if (!FORMSPREE_ENDPOINT) {
            // No endpoint configured yet — graceful fallback.
            setErrorMsg(
                "Le service d'envoi de message n'est pas encore configuré. Merci de nous écrire directement à info@fiduciaire-bleich.ch."
            );
            setStatus("error");
            return;
        }

        try {
            const res = await fetch(FORMSPREE_ENDPOINT, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    subject: form.subject,
                    message: form.message,
                    _subject: `Nouveau message — ${form.subject} — ${form.name}`,
                }),
            });

            if (res.ok) {
                setStatus("success");
                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                    consent: false,
                });
            } else {
                const data = await res.json().catch(() => ({}));
                setErrorMsg(
                    data?.errors?.[0]?.message ||
                        "Une erreur est survenue lors de l'envoi. Merci de réessayer ou de nous écrire directement à info@fiduciaire-bleich.ch."
                );
                setStatus("error");
            }
        } catch {
            setErrorMsg(
                "Une erreur réseau est survenue. Merci de réessayer ou de nous écrire directement à info@fiduciaire-bleich.ch."
            );
            setStatus("error");
        }
    };

    return (
        <>
            <SEO
                title="Contact — Fiduciaire Bleich, Rue de Rive 8 Genève"
                description="Contactez la Fiduciaire Bleich. Rue de Rive 8, 1204 Genève. +41 22 319 34 40. Premier entretien gratuit et confidentiel."
                canonical="https://fiduciaire-bleich.ch/contact"
            />

            {/* HERO */}
            <section
                data-testid="contact-hero"
                className="bg-white py-20 lg:py-28 border-b border-[#E5E7EB]"
            >
                <div className="container-prose max-w-3xl">
                    <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                        <span className="w-8 h-px bg-[#1C9CAC]" />
                        Contact
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl text-[#2C3E45] leading-[1.05] tracking-tight">
                        Prenons <span className="italic text-[#1C9CAC]">rendez-vous.</span>
                    </h1>
                    <p className="mt-6 text-lg text-[#5C6B70] leading-relaxed">
                        Premier entretien gratuit et confidentiel, en nos bureaux ou en
                        visioconférence.
                    </p>
                </div>
            </section>

            {/* COORDONNÉES + FORMULAIRE */}
            <section
                data-testid="contact-main"
                className="bg-[#FAFAF7] py-20 lg:py-24 border-b border-[#E5E7EB]"
            >
                <div className="container-prose grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* COORDONNÉES */}
                    <aside className="lg:col-span-5 space-y-10">
                        <div>
                            <h2 className="font-serif text-3xl text-[#2C3E45] mb-2">
                                Nous contacter
                            </h2>
                            <div className="w-12 h-px bg-[#1C9CAC]" />
                        </div>

                        <div className="space-y-8 text-[15px]">
                            <ContactBlock icon={MapPin} title="Adresse">
                                <p>Fiduciaire Bleich Sàrl</p>
                                <p>Rue de Rive 8</p>
                                <p>1204 Genève, Suisse</p>
                            </ContactBlock>

                            <ContactBlock icon={Phone} title="Téléphone">
                                <a
                                    href="tel:+41223193440"
                                    data-testid="contact-phone-link"
                                    className="hover:text-[#1C9CAC] transition-colors"
                                >
                                    +41 22 319 34 40
                                </a>
                            </ContactBlock>

                            <ContactBlock icon={Mail} title="Email">
                                <a
                                    href="mailto:info@fiduciaire-bleich.ch"
                                    data-testid="contact-email-link"
                                    className="hover:text-[#1C9CAC] transition-colors break-all"
                                >
                                    info@fiduciaire-bleich.ch
                                </a>
                            </ContactBlock>

                            <ContactBlock icon={Clock} title="Horaires">
                                <p>Lundi à vendredi</p>
                                <p>9h00 – 18h00</p>
                                <p className="text-[#5C6B70] text-sm mt-1">
                                    (Visites sur rendez-vous)
                                </p>
                            </ContactBlock>

                            <ContactBlock icon={Globe2} title="Langues">
                                <p>Français · Anglais</p>
                            </ContactBlock>
                        </div>
                    </aside>

                    {/* FORMULAIRE */}
                    <div className="lg:col-span-7">
                        <div className="bg-white border border-[#E5E7EB] p-8 lg:p-10">
                            <h2 className="font-serif text-3xl text-[#2C3E45] mb-2">
                                Envoyez-nous un message
                            </h2>
                            <div className="w-12 h-px bg-[#1C9CAC] mb-8" />

                            {status === "success" ? (
                                <div
                                    data-testid="contact-success"
                                    className="flex items-start gap-4 p-6 border border-[#1C9CAC]/30 bg-[#1C9CAC]/5"
                                >
                                    <CheckCircle2
                                        className="text-[#1C9CAC] flex-shrink-0 mt-0.5"
                                        size={22}
                                        strokeWidth={1.5}
                                    />
                                    <div>
                                        <p className="font-serif text-xl text-[#2C3E45] mb-1">
                                            Merci, votre message a bien été envoyé.
                                        </p>
                                        <p className="text-[#5C6B70] text-[15px]">
                                            Nous vous répondrons dans les meilleurs délais.
                                        </p>
                                        <button
                                            type="button"
                                            data-testid="contact-send-another"
                                            onClick={() => setStatus("idle")}
                                            className="mt-4 text-[#1C9CAC] hover:text-[#16818F] text-sm font-medium underline underline-offset-4"
                                        >
                                            Envoyer un autre message
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form
                                    data-testid="contact-form"
                                    onSubmit={handleSubmit}
                                    noValidate
                                    className="space-y-6"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="name" className="form-label">
                                                Nom et prénom <span className="text-[#1C9CAC]">*</span>
                                            </label>
                                            <input
                                                id="name"
                                                name="name"
                                                type="text"
                                                required
                                                value={form.name}
                                                onChange={handleChange}
                                                data-testid="form-name"
                                                className="form-input"
                                                autoComplete="name"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="form-label">
                                                Email <span className="text-[#1C9CAC]">*</span>
                                            </label>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={handleChange}
                                                data-testid="form-email"
                                                className="form-input"
                                                autoComplete="email"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="phone" className="form-label">
                                                Téléphone
                                            </label>
                                            <input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={handleChange}
                                                data-testid="form-phone"
                                                className="form-input"
                                                autoComplete="tel"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="subject" className="form-label">
                                                Sujet <span className="text-[#1C9CAC]">*</span>
                                            </label>
                                            <select
                                                id="subject"
                                                name="subject"
                                                required
                                                value={form.subject}
                                                onChange={handleChange}
                                                data-testid="form-subject"
                                                className="form-input bg-white appearance-none pr-10"
                                                style={{
                                                    backgroundImage:
                                                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235C6B70' stroke-width='2'><polyline points='6 9 12 15 18 9'></polyline></svg>\")",
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "right 1rem center",
                                                }}
                                            >
                                                <option value="" disabled>
                                                    — Choisir un sujet —
                                                </option>
                                                {subjects.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="form-label">
                                            Message <span className="text-[#1C9CAC]">*</span>
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={5}
                                            required
                                            value={form.message}
                                            onChange={handleChange}
                                            data-testid="form-message"
                                            className="form-input resize-y min-h-[140px]"
                                        />
                                    </div>

                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="consent"
                                            checked={form.consent}
                                            onChange={handleChange}
                                            data-testid="form-consent"
                                            className="mt-1 w-4 h-4 accent-[#1C9CAC] cursor-pointer flex-shrink-0"
                                        />
                                        <span className="text-sm text-[#5C6B70] leading-relaxed">
                                            J'accepte que mes données soient utilisées uniquement
                                            pour répondre à ma demande.
                                        </span>
                                    </label>

                                    {status === "error" && errorMsg && (
                                        <div
                                            data-testid="contact-error"
                                            role="alert"
                                            className="flex items-start gap-3 p-4 border border-red-300 bg-red-50 text-red-700 text-sm"
                                        >
                                            <AlertCircle
                                                size={18}
                                                strokeWidth={1.75}
                                                className="flex-shrink-0 mt-0.5"
                                            />
                                            <p>{errorMsg}</p>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="form-submit"
                                        disabled={status === "sending"}
                                        className="btn-primary w-full sm:w-auto"
                                    >
                                        {status === "sending" ? "Envoi en cours…" : "Envoyer"}
                                        {status !== "sending" && (
                                            <Send size={16} strokeWidth={1.75} />
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* PLAN D'ACCÈS */}
            <section
                data-testid="contact-map-section"
                className="bg-white py-20 lg:py-24"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-12">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Plan d'accès
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Nous <span className="italic">trouver.</span>
                        </h2>
                    </div>

                    <div className="aspect-[16/9] w-full border border-[#E5E7EB] overflow-hidden">
                        <iframe
                            title="Plan d'accès — Rue de Rive 8, 1204 Genève"
                            data-testid="contact-map-iframe"
                            src="https://www.google.com/maps?q=Rue+de+Rive+8,+1204+Gen%C3%A8ve,+Suisse&z=16&output=embed"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allowFullScreen
                        />
                    </div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Bus,
                                title: "Transports",
                                desc: "Arrêt Métropole à 2 minutes",
                            },
                            {
                                icon: ParkingCircle,
                                title: "Parking",
                                desc: "Parking du Mont-Blanc à 400 m",
                            },
                            {
                                icon: Train,
                                title: "Gare Cornavin",
                                desc: "15 minutes à pied ou bus 8",
                            },
                        ].map(({ icon: Icon, title, desc }) => (
                            <div
                                key={title}
                                data-testid={`access-block-${title.toLowerCase()}`}
                                className="flex items-start gap-4 p-6 border border-[#E5E7EB] bg-[#FAFAF7]"
                            >
                                <Icon
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-[#1C9CAC] mt-0.5 flex-shrink-0"
                                />
                                <div>
                                    <p className="font-serif text-lg text-[#2C3E45]">{title}</p>
                                    <p className="text-sm text-[#5C6B70] mt-1">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

const ContactBlock = ({ icon: Icon, title, children }) => (
    <div className="flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center border border-[#1C9CAC]/30 text-[#1C9CAC] flex-shrink-0">
            <Icon size={18} strokeWidth={1.5} />
        </div>
        <div className="space-y-0.5 text-[#2C3E45]">
            <p className="font-serif text-lg leading-tight mb-1">{title}</p>
            {children}
        </div>
    </div>
);

export default Contact;
